import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceOfferRedemptionType,
  useLoyaltyServiceAnonymousOfferQuery,
  useLoyaltyServiceOfferQuery,
} from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { LoyaltyEngineId, appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';

import { mapServiceOffer } from '../utils';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

export function useLoyaltyServiceOffer({
  loyaltyEngineId,
  skip = false,
}: {
  loyaltyEngineId: LoyaltyEngineId;
  skip?: boolean;
}) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const variables = {
    id: loyaltyEngineId || '',
    serviceMode: getLoyaltyServiceServiceMode(serviceMode),
    storeId,
    redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
    includeOfferGroups: true,
    omitInvalids: false,
  };

  const skipQueries = skip || !loyaltyEngineId || loyaltyUserIdLoading;

  const {
    data: loyaltyServiceOfferData,
    loading: loyaltyServiceOfferLoading,
    refetch: refetchLoyaltyServiceOffer,
  } = useLoyaltyServiceOfferQuery({
    variables,
    skip: skipQueries || !loyaltyUserId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const {
    data: loyaltyServiceAnonymousOfferData,
    loading: loyaltyServiceAnonymousOfferLoading,
    refetch: refetchLoyaltyServiceAnonymousOffer,
  } = useLoyaltyServiceAnonymousOfferQuery({
    variables,
    skip: skipQueries || Boolean(loyaltyUserId),
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const selectedIncentive = useMemo(() => {
    const serviceOffer = loyaltyUserId
      ? loyaltyServiceOfferData?.loyaltyServiceOffer
      : loyaltyServiceAnonymousOfferData?.loyaltyServiceAnonymousOffer;

    if (!serviceOffer) {
      return undefined;
    }

    return appOfferToLoyaltyOffer(mapServiceOffer(serviceOffer));
  }, [loyaltyServiceAnonymousOfferData, loyaltyServiceOfferData, loyaltyUserId]);

  const isIncentivesLoading =
    !skip &&
    (loyaltyUserIdLoading ||
      (loyaltyUserId ? loyaltyServiceOfferLoading : loyaltyServiceAnonymousOfferLoading));

  const refreshOffer = useCallback(async () => {
    if (skipQueries) {
      return;
    }
    if (loyaltyUserId) {
      await refetchLoyaltyServiceOffer();
      return;
    }
    await refetchLoyaltyServiceAnonymousOffer();
  }, [loyaltyUserId, refetchLoyaltyServiceAnonymousOffer, refetchLoyaltyServiceOffer, skipQueries]);

  return {
    selectedIncentive,
    isIncentivesLoading,
    refreshOffer,
  };
}
